import { useEffect, useState } from "react";
import { createLogger } from '../../../infra/logger';

const log = createLogger('@breath-lab/landing');
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getServices } from '@breath-lab/services';
import { PublicJourney } from "../../../domain/models";
import { ROUTES, journeyDetailPath } from "../../../routes";
import Button from "@ui/components/Button/Button";
import FadeInOnScroll from "@ui/components/FadeInOnScroll/FadeInOnScroll";
import MediaCard from "@ui/components/MediaCard/MediaCard";
import { logEvent } from "../../../infra/firebase/analytics";
import { AnalyticsEvent, AnalyticsParam } from "../../../domain/analytics";
import styles from "./JourneysSection.module.css";

const JOURNEY_COVER_IMAGES: Record<string, string> = {
  STILL_WATERS: "/journeys/still-waters.webp",
  MORNING_FIRE: "/journeys/morning-fire.webp",
  NIGHT_TIDE: "/journeys/night-tide.webp",
  THE_SHARPENED_MIND: "/journeys/sharpened-mind.webp",
  ROOTS_OF_PRANA: "/journeys/roots-of-prana.webp",
  CENTER_GROUND: "/journeys/center-ground.webp",
  QUIET_FURNACE: "/journeys/quiet-furnace.webp",
  THE_SETTLING: "/journeys/the-settling.webp",
  FIVE_DIMENSIONS_OF_PRANAYAMA: "/journeys/five-dimensions-of-pranayama.webp",
};

const FEATURED_JOURNEY_IDS = [
  'FIVE_DIMENSIONS_OF_PRANAYAMA',
  'NIGHT_TIDE',
  'THE_SHARPENED_MIND',
  'QUIET_FURNACE',
];

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const JOURNEY_SKELETON_COUNT = 4;

const JOURNEY_STATIC_LINKS = [
  { id: 'STILL_WATERS', label: 'Still Waters' },
  { id: 'MORNING_FIRE', label: 'Morning Fire' },
  { id: 'NIGHT_TIDE', label: 'Night Tide' },
  { id: 'THE_SHARPENED_MIND', label: 'The Sharpened Mind' },
  { id: 'ROOTS_OF_PRANA', label: 'Roots of Prana' },
  { id: 'CENTER_GROUND', label: 'Center Ground' },
  { id: 'QUIET_FURNACE', label: 'Quiet Furnace' },
  { id: 'THE_SETTLING', label: 'The Settling' },
];

export default function JourneysSection() {
  const { t } = useTranslation('landing');
  const ctaHref = ROUTES.journeys;
  const [journeys, setJourneys] = useState<PublicJourney[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getServices().data
      .fetchPublicJourneys()
      .then((data) => {
        if (!cancelled) {
          setJourneys(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        log.error('Failed to load journeys', err);
        if (!cancelled) { setLoading(false); setError(true); }
      });
    return () => {
      cancelled = true;
    };
  }, [retryKey]);

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <h2 className={styles.heading}>
          {t('journeys.heading')}
        </h2>
        <p className={styles.contextLine}>
          {t('journeys.contextLine')}
        </p>

        {loading ? (
          <>
            <ul className={styles.grid} role="list">
              {Array.from({ length: JOURNEY_SKELETON_COUNT }).map((_, i) => (
                <li key={i} className={styles.skeleton} />
              ))}
            </ul>
            <nav className={styles.staticLinks} aria-label="All journeys">
              {JOURNEY_STATIC_LINKS.map((j, i) => (
                <span key={j.id}>
                  {i > 0 && <span aria-hidden="true"> · </span>}
                  <Link to={journeyDetailPath(j.id)}>{j.label}</Link>
                </span>
              ))}
            </nav>
          </>
        ) : error ? (
          <div className={styles.errorState}>
            <p>{t('journeys.errorMessage')}</p>
            <Button variant="ghost" onClick={() => setRetryKey((k) => k + 1)}>
              {t('journeys.retry')}
            </Button>
          </div>
        ) : (
          <ul className={styles.grid} role="list">
            {FEATURED_JOURNEY_IDS
              .map((id) => journeys.find((j) => j.id === id))
              .filter((j): j is PublicJourney => j !== undefined)
              .map((journey, i) => (
              <li key={journey.id} onClick={() => logEvent(AnalyticsEvent.landingJourneyCardClick, { [AnalyticsParam.journeyId]: journey.id })}>
                <FadeInOnScroll delay={i * 0.1} className={styles.fadeItem}>
                  <MediaCard
                    image={JOURNEY_COVER_IMAGES[journey.id]}
                    imageAlt={journey.name}
                    title={journey.name}
                    meta={`${capitalise(journey.difficulty)} · ${journey.totalSteps} steps · ${journey.category}`}
                    href={journeyDetailPath(journey.id)}
                  />
                </FadeInOnScroll>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.cta}>
          <Button variant="secondary" as={Link} to={ctaHref} onClick={() => logEvent(AnalyticsEvent.landingExploreJourneysClick)}>
            {t('journeys.exploreAll')}
          </Button>
        </div>
      </div>
    </section>
  );
}
